import { DISPLAY_NAME_MAX, normalizeDisplayName } from "../../api/src/shared/display-name.js";
import { el } from "../lib/dom.js";
import { ApiError } from "../lib/http.js";
import type { HeldWrite, WriteGate } from "./listPolicy.js";
import { policyLinks } from "./policyLinks.js";
import { actionRow, type Popup, type ScreenHost } from "./ui.js";

/**
 * The name a shared list asks for before a first change (g72).
 *
 * Reading never asks: a link opens its list at once. The first attempt to
 * change it, by whatever way -- a tick, a save, a drag, a delete, an Add --
 * is held here while the visitor says who they are, and then runs once, if
 * it still means what it meant. Cancel runs nothing. A guest's name is
 * remembered by this browser across every shared list; a signed-in visitor
 * names their account.
 */

/** The words, exported so the tests pin them. */
export const NAME_PROMPT = {
  title: "Your name",
  renameTitle: "Change name",
  label: "Your name",
  explain: "People on this list will see this name next to what you add.",
  save: "Continue",
  rename: "Save",
  cancel: "Cancel",
  empty: "Enter a name.",
  tooLong: `Use ${DISPLAY_NAME_MAX} characters or fewer.`,
  invalid: "Use letters, numbers, spaces and punctuation only.",
  offline: "Can't reach Trayflux. Saving your name needs a connection. Your change is still here.",
  limited: "Too many name changes. Try again later.",
  expired: "Your sign-in has expired. Sign in again to add as yourself, or continue with a name.",
  signIn: "Sign in again",
} as const;

/** What the page knows about the visitor, and what it can do about their name. */
export interface NamePort {
  /** Who is looking: a signed-in account, a remembered guest, or nobody named yet. */
  visitor(): { kind: "account" | "guest" | "anonymous"; name: string | null; sessionExpired?: boolean };
  /** Whether a change may run now: an edit link that works, and a visitor with a name. */
  ready(): boolean;
  /** Whether the page can change anything at all: an edit link that still works. */
  editable(): boolean;
  /**
   * Saves the name: the account's for a signed-in visitor, the guest's
   * otherwise, and for someone not yet named, the scope they now write
   * under. Rejects with the server's refusal or a network error; resolves
   * only once a change can run.
   */
  name(displayName: string): Promise<void>;
  /** Offered when a sign-in has expired. */
  signIn?(): void;
}

export interface NameGate extends WriteGate {
  /** The menu's Change name: the same prompt, with nothing held. */
  changeName(returnFocusTo?: HTMLElement): void;
  /** True while the prompt is up, so a second attempt is not asked twice. */
  asking(): boolean;
}

export function nameGate(host: Pick<ScreenHost, "openPopup" | "announce">, port: NamePort): NameGate {
  /** The one held change: a second attempt while asking replaces nothing. */
  let held: HeldWrite | undefined;
  let open: { popup: Popup; within: boolean } | undefined;

  function refuse(): void {
    // Only reached when a control the page should not have offered was used:
    // the link ended under it, or it can only view.
    host.announce("This link can't make changes.");
  }

  function prompt(write: HeldWrite | undefined, returnFocusTo?: HTMLElement): void {
    const visitor = port.visitor();
    const renaming = write === undefined;
    const field = el("input", {
      attrs: {
        id: "visitor-name",
        type: "text",
        autocomplete: "name",
        maxlength: DISPLAY_NAME_MAX * 2,
        value: renaming || visitor.sessionExpired ? visitor.name ?? "" : "",
        "aria-describedby": "visitor-name-explain visitor-name-error",
      },
    });
    const error = el("p", { class: "field-error", attrs: { id: "visitor-name-error", hidden: true } });
    const submit = el("button", { text: renaming ? NAME_PROMPT.rename : NAME_PROMPT.save, attrs: { type: "submit" } });
    const cancel = el("button", { class: "secondary", text: NAME_PROMPT.cancel, attrs: { type: "button" } });
    const signIn = visitor.sessionExpired && port.signIn
      ? el("button", { class: "secondary", text: NAME_PROMPT.signIn, attrs: { type: "button" }, on: { click: () => { finish(false); port.signIn?.(); } } })
      : undefined;
    let busy = false;

    const say = (message: string | undefined) => {
      error.textContent = message ?? "";
      error.hidden = message === undefined;
      if (message === undefined) field.removeAttribute("aria-invalid");
      else {
        field.setAttribute("aria-invalid", "true");
        host.announce(message);
      }
    };

    const form = el("form", {
      class: "visitor-name item-form",
      attrs: { novalidate: true },
      children: [
        el("p", { class: "muted", attrs: { id: "visitor-name-explain" }, text: visitor.sessionExpired ? NAME_PROMPT.expired : NAME_PROMPT.explain }),
        el("div", { class: "field", children: [el("label", { class: "field-label", text: NAME_PROMPT.label, attrs: { for: "visitor-name" } }), field, error] }),
        actionRow([cancel, signIn], submit),
        // A visitor gives a name here, so this is where they can read what is kept (g96).
        policyLinks(),
      ],
      on: { submit: (event) => { event.preventDefault(); void save(); } },
    });
    cancel.addEventListener("click", () => finish(false));

    async function save(): Promise<void> {
      // A second press while the first is on its way does nothing.
      if (busy) return;
      const verdict = normalizeDisplayName(field.value);
      if (!verdict.ok) { say(verdict.code === "too_long" ? NAME_PROMPT.tooLong : NAME_PROMPT.invalid); field.focus(); return; }
      if (verdict.name === null) { say(NAME_PROMPT.empty); field.focus(); return; }
      busy = true;
      submit.disabled = true;
      cancel.disabled = true;
      form.setAttribute("aria-busy", "true");
      say(undefined);
      try {
        await port.name(verdict.name);
      } catch (failure: unknown) {
        busy = false;
        submit.disabled = false;
        cancel.disabled = false;
        form.removeAttribute("aria-busy");
        if (failure instanceof ApiError && failure.code === "share_unavailable") {
          // The link ended while asking: nothing can run, and the page says why.
          finish(false);
          return;
        }
        // Status 0 is a request that never reached the server.
        say(failure instanceof ApiError && failure.status !== 0
          ? failure.status === 429 ? NAME_PROMPT.limited : failure.message
          : NAME_PROMPT.offline);
        field.focus();
        return;
      }
      host.announce(`Adding as ${verdict.name}.`);
      finish(true);
    }

    /**
     * Ends the prompt, once. The held change is let go of before it runs,
     * so nothing -- a second press, a late answer -- can run it twice.
     */
    function finish(named: boolean): void {
      const surface = open;
      if (!surface) return;
      open = undefined;
      const write = held;
      held = undefined;
      if (surface.within) surface.popup.resume();
      else surface.popup.close();
      if (!write) return;
      if (!named) {
        write.cancelled?.();
        return;
      }
      if (!port.ready() || !write.valid()) {
        write.cancelled?.();
        return;
      }
      write.run();
    }

    const title = renaming ? NAME_PROMPT.renameTitle : NAME_PROMPT.title;
    if (write?.within?.isOpen()) {
      // Escape, Back and the close button while the name is on its way do
      // nothing, as the prompt's own popup refuses to close while busy: a
      // cancel then would drop the change and still save the name.
      write.within.step({ title, body: form, focus: field, cancel: () => { if (!busy) finish(false); } });
      open = { popup: write.within, within: true };
      return;
    }
    const popup = host.openPopup({
      title,
      body: form,
      focus: field,
      busy: () => busy,
      returnFocusTo: () => write?.returnFocusTo?.() ?? returnFocusTo,
      // Every way out but Continue is Cancel.
      onClose: () => finish(false),
    });
    open = { popup, within: false };
  }

  return {
    ready: () => port.ready(),
    hold(write) {
      if (!port.editable()) {
        refuse();
        write.cancelled?.();
        return;
      }
      if (port.ready()) {
        write.run();
        return;
      }
      if (open) {
        // Already asking about another change: this one is not queued behind
        // it, where it would run unseen once the name was given.
        write.cancelled?.();
        return;
      }
      held = write;
      prompt(write);
    },
    changeName(returnFocusTo) {
      if (open) return;
      prompt(undefined, returnFocusTo);
    },
    asking: () => open !== undefined,
  };
}
